import { useCallback, useEffect, useState } from 'react'
import {
  type CarryTradePosition,
  type CarryTradePositionsResponse,
  fetchCarryPositions,
} from '../../lib/api.ts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

function formatTimeRemaining(endDate: string): { text: string; urgent: boolean; pct: number } {
  const now = Date.now()
  const end = new Date(endDate).getTime()
  const diffMs = end - now

  if (diffMs <= 0) return { text: 'Expired', urgent: true, pct: 0 }

  const totalMs = 60 * 60 * 1000 // treat 1h as full bar
  const pct = Math.min(100, (diffMs / totalMs) * 100)
  const urgent = diffMs < 60_000

  const diffMin = Math.floor(diffMs / 60_000)
  const diffSec = Math.floor((diffMs % 60_000) / 1_000)

  if (diffMin >= 60) {
    const h = Math.floor(diffMin / 60)
    const m = diffMin % 60
    return { text: `${h}h ${m}m`, urgent: false, pct }
  }

  return { text: `${diffMin}m ${String(diffSec).padStart(2, '0')}s`, urgent, pct }
}

function pnlClass(value: number | null | undefined) {
  if ((value ?? 0) > 0) return 'pnl-positive'
  if ((value ?? 0) < 0) return 'pnl-negative'
  return 'pnl-neutral'
}

function formatPnL(value: number | null | undefined): string {
  const v = value ?? 0
  const sign = v >= 0 ? '+' : ''
  return `${sign}$${Math.abs(v).toFixed(2)}`
}

function StatusBadge({ status }: { status: CarryTradePosition['status'] }) {
  const map: Record<CarryTradePosition['status'], { label: string; variant: 'default' | 'warning' | 'blue' | 'secondary' | 'destructive' }> = {
    MERGED: { label: 'MERGED', variant: 'default' },
    FAILED: { label: 'FAILED', variant: 'destructive' },
    HEDGED: { label: 'HEDGED', variant: 'default' },
    HAS_HEDGE: { label: 'HAS ENTRY', variant: 'warning' },
    HAS_ENTRY: { label: 'HAS ENTRY', variant: 'warning' },
    WAITING_ENTRY: { label: 'WAITING', variant: 'blue' },
    PUMPED: { label: 'PUMPED', variant: 'warning' },
    CLOSED: { label: 'CLOSED', variant: 'secondary' },
  }
  const cfg = map[status] ?? { label: status, variant: 'secondary' as const }
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>
}

function PositionRow({ pos }: { pos: CarryTradePosition }) {
  const time = formatTimeRemaining(pos.endDate)

  return (
    <tr>
      <td>
        <span className="font-mono" style={{ fontSize: '11px', color: 'var(--text-primary)' }}>
          {pos.marketSlug.length > 32 ? pos.marketSlug.slice(0, 30) + '…' : pos.marketSlug}
        </span>
      </td>
      <td><StatusBadge status={pos.status} /></td>
      <td className="num">
        <span className="mono-num">${(pos.totalCost ?? pos.totalInvested ?? 0).toFixed(2)}</span>
      </td>
      <td className="num">
        <span className="mono-num">${(pos.currentValue ?? 0).toFixed(2)}</span>
      </td>
      <td className="num">
        {pos.arbSpread !== undefined ? (
          <span className="mono-num" style={{ color: 'var(--text-secondary)' }}>
            ${pos.arbSpread.toFixed(3)}
          </span>
        ) : (
          <span className="mono-num text-muted">-</span>
        )}
      </td>
      <td className="num">
        <span className={pnlClass(pos.status === 'MERGED' ? (pos.realizedPnL ?? (1.0 - (pos.totalCost ?? pos.totalInvested ?? 0))) : 0)}>
          {formatPnL(pos.status === 'MERGED' ? (pos.realizedPnL ?? (1.0 - (pos.totalCost ?? pos.totalInvested ?? 0))) : 0)}
        </span>
      </td>
      <td>
        {pos.mergeTxHash ? (
          <a
            href={`https://polygonscan.com/tx/${pos.mergeTxHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono" style={{ fontSize: '11px', textDecoration: 'underline', color: 'var(--blue)' }}
          >
            {pos.mergeTxHash.slice(0, 6)}…{pos.mergeTxHash.slice(-4)}
          </a>
        ) : (
          <span className="text-muted">-</span>
        )}
      </td>
      <td>
        <div style={{ minWidth: '80px' }}>
          <div className="font-mono" style={{ fontSize: '10px', color: time.urgent ? 'var(--red)' : 'var(--text-secondary)' }}>
            {time.text}
          </div>
          <div className="progress-bar" style={{ marginTop: '3px' }}>
            <div
              className={`progress-bar-fill ${time.urgent ? 'urgent' : time.pct < 30 ? 'warning' : 'normal'}`}
              style={{ width: `${time.pct}%` }}
            />
          </div>
        </div>
      </td>
    </tr>
  )
}

const PAGE_SIZE = 10

export function Positions() {
  const [data, setData] = useState<CarryTradePositionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    try {
      const res = await fetchCarryPositions()
      setData(res)
      setPage(1)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 3000)
    return () => clearInterval(interval)
  }, [load])

  const positions = data?.positions ?? []
  const stats = data?.stats

  const totalPages = Math.max(1, Math.ceil(positions.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageStart = (safePage - 1) * PAGE_SIZE
  const pageEnd = Math.min(pageStart + PAGE_SIZE, positions.length)
  const pagePositions = positions.slice(pageStart, pageEnd)

  return (
    <div className="animate-fade-in-up">
      <div className="page-header">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-title-main">Arbitrage Event Log</div>
        </div>
        <div className="page-actions">
          <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            Auto-refresh 3s
          </span>
          <span className="live-dot animate-pulse-dot" style={{ color: 'var(--green)', fontSize: '8px', marginLeft: '4px' }}>●</span>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Active</div>
              <div className="stat-card-value">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Total Invested</div>
              <div className="stat-card-value">${(stats.totalInvested ?? 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Unrealized P&L</div>
              <div className={`stat-card-value ${pnlClass(stats.totalUnrealizedPnL)}`}>
                {formatPnL(stats.totalUnrealizedPnL)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="stat-card-label">Realized P&L</div>
              <div className={`stat-card-value ${pnlClass(stats.totalRealizedPnL)}`}>
                {formatPnL(stats.totalRealizedPnL)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Event Log</span>
            {loading && (
              <span className="font-mono" style={{ fontSize: '9px', color: 'var(--text-muted)' }}>LOADING…</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <div style={{ padding: '16px', color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
              Error: {error}
            </div>
          )}

          {!error && positions.length === 0 && !loading && (
            <div className="empty-state">
              <span className="empty-state-text">
                No events found
                <span className="empty-state-cursor animate-blink" style={{ color: 'var(--green)' }}>▋</span>
              </span>
            </div>
          )}

          {positions.length > 0 && (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>Status</th>
                      <th className="num">Total Cost</th>
                      <th className="num">Value</th>
                      <th className="num">Exp. Spread</th>
                      <th className="num">Profit</th>
                      <th>Tx Hash</th>
                      <th>Time Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagePositions.map((pos) => (
                      <PositionRow key={pos.id} pos={pos} />
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderTop: '1px solid var(--border-subtle)',
                }}>
                  <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                    {pageStart + 1}–{pageEnd} of {positions.length}
                  </span>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                    >
                      ← Prev
                    </button>
                    <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-secondary)', padding: '0 8px' }}>
                      {safePage} / {totalPages}
                    </span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
